import React, { useEffect, useState } from "react";

// WMO weather code → [label, icon]. Open-Meteo returns these on daily and hourly.
export const WX = {
  0: ["Clear", "☀️"], 1: ["Mainly clear", "🌤️"], 2: ["Partly cloudy", "⛅"], 3: ["Overcast", "☁️"],
  45: ["Fog", "🌫️"], 48: ["Fog", "🌫️"],
  51: ["Light drizzle", "🌦️"], 53: ["Drizzle", "🌦️"], 55: ["Heavy drizzle", "🌦️"],
  61: ["Light rain", "🌧️"], 63: ["Rain", "🌧️"], 65: ["Heavy rain", "🌧️"],
  66: ["Freezing rain", "🌧️"], 67: ["Freezing rain", "🌧️"],
  71: ["Light snow", "🌨️"], 73: ["Snow", "🌨️"], 75: ["Heavy snow", "🌨️"], 77: ["Snow grains", "🌨️"],
  80: ["Showers", "🌦️"], 81: ["Showers", "🌦️"], 82: ["Heavy showers", "⛈️"],
  85: ["Snow showers", "🌨️"], 86: ["Snow showers", "🌨️"],
  95: ["Thunderstorm", "⛈️"], 96: ["Thunderstorm", "⛈️"], 99: ["Thunderstorm", "⛈️"]
};

// One daily+hourly forecast for the given coords (Open-Meteo, free, no key), covering
// the next 11 days. One request serves every event in a squadron (shared coords).
// Returns { daily: {date: {...}}, hourly: {isoHour: {...}} } or null.
export function useForecast(weather) {
  const [forecast, setForecast] = useState(null);
  const lat = weather?.lat;
  const lon = weather?.lon;
  useEffect(() => {
    setForecast(null);
    if (lat == null || lon == null) return;
    let live = true;
    (async () => {
      try {
        const r = await fetch(`https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&forecast_days=11&temperature_unit=fahrenheit&timezone=auto&daily=weather_code,temperature_2m_max,temperature_2m_min,apparent_temperature_max,apparent_temperature_min,precipitation_probability_max&hourly=weather_code,temperature_2m,apparent_temperature,precipitation_probability`).then((x) => x.json());
        if (!live) return;
        const round = (arr, i) => (arr ? Math.round(arr[i]) : null);
        const daily = {};
        (r.daily?.time || []).forEach((t, i) => {
          daily[t] = { code: r.daily.weather_code[i], hi: round(r.daily.temperature_2m_max, i), lo: round(r.daily.temperature_2m_min, i), feelsHi: round(r.daily.apparent_temperature_max, i), feelsLo: round(r.daily.apparent_temperature_min, i), precip: r.daily.precipitation_probability_max?.[i] };
        });
        const hourly = {};
        (r.hourly?.time || []).forEach((t, i) => {
          hourly[t] = { code: r.hourly.weather_code[i], temp: round(r.hourly.temperature_2m, i), feels: round(r.hourly.apparent_temperature, i), precip: r.hourly.precipitation_probability?.[i] };
        });
        setForecast({ daily, hourly });
      } catch { /* offline or API down — no weather */ }
    })();
    return () => { live = false; };
  }, [lat, lon]);
  return forecast;
}

// Pick the cell for a session. Hourly (precise time) within 72h, daily hi/lo range out
// to 10 days, nothing beyond that or in the past. startsAt is "YYYY-MM-DD HH:MM:SS"; we
// match by wall-clock so an 0700 session reads the location's 0700 forecast.
export function pickForecast(forecast, startsAt) {
  if (!forecast || !startsAt) return null;
  const date = startsAt.slice(0, 10);
  const hour = startsAt.slice(11, 13);
  const hoursOut = (new Date(startsAt.replace(" ", "T")) - Date.now()) / 3600000;
  if (hoursOut < -1) return null;
  if (hoursOut <= 72) {
    const h = forecast.hourly[`${date}T${hour}:00`];
    if (h) return { kind: "hour", time: `${hour}:00`, code: h.code, temp: h.temp, feels: h.feels, precip: h.precip };
  }
  if (hoursOut <= 240) {
    const d = forecast.daily[date];
    if (d) return { kind: "day", code: d.code, hi: d.hi, lo: d.lo, feelsHi: d.feelsHi, feelsLo: d.feelsLo, precip: d.precip };
  }
  return null;
}

// Approximate heat/cold advisory from the feels-like temperature (which already
// folds in humidity + wind). NOT an official WBGT flag — labeled "approx." so it
// reads as a planning cue, not a regulatory reading. Heat bands ≈ NWS heat-index
// guidance; cold bands ≈ frostbite-risk wind-chill. Returns null when conditions
// are unremarkable. ponytail: thresholds in one place — tune here.
export function heatAdvisory(wx) {
  if (!wx) return null;
  const hot = wx.kind === "hour" ? wx.feels : wx.feelsHi;
  const cold = wx.kind === "hour" ? wx.feels : wx.feelsLo;
  if (hot != null) {
    if (hot >= 105) return { tone: "black", label: "Black flag", note: "Suspend strenuous outdoor PT" };
    if (hot >= 95) return { tone: "red", label: "Red flag", note: "Limit intense work; full hydration" };
    if (hot >= 88) return { tone: "yellow", label: "Heat caution", note: "Hydrate; watch for heat illness" };
  }
  if (cold != null) {
    if (cold <= 10) return { tone: "blue", label: "Extreme cold", note: "Cover skin; limit exposure" };
    if (cold <= 25) return { tone: "blue", label: "Cold caution", note: "Layer up; frostbite risk" };
  }
  return null;
}

const ADVISORY_STYLE = {
  black: "bg-ink text-white",
  red: "bg-red-100 text-red-800 border border-red-300",
  yellow: "bg-amber-100 text-amber-900 border border-amber-300",
  blue: "bg-sky-100 text-sky-800 border border-sky-300"
};
const ADVISORY_ICON = { black: "⬛", red: "🟥", yellow: "🟧", blue: "🟦" };

// Small advisory pill. `full` adds the guidance note (event detail); compact omits
// it (calendar card). Renders nothing when there's no advisory.
export function HeatAdvisoryChip({ wx, full }) {
  const adv = heatAdvisory(wx);
  if (!adv) return null;
  return (
    <span
      title={`${adv.note} — approximate, not an official WBGT reading`}
      className={`mono text-[9px] font-semibold uppercase tracking-[0.1em] px-1.5 py-0.5 inline-flex items-center gap-1 ${ADVISORY_STYLE[adv.tone]}`}
    >
      {ADVISORY_ICON[adv.tone]} {adv.label}{full ? ` · ${adv.note}` : ""} <span className="opacity-60">(approx.)</span>
    </span>
  );
}

// ponytail: thresholds are wall-clock approximate (browser tz vs location tz); fine at
// 24–72h granularity. Tighten only if squadrons span far-flung time zones.
if (typeof process !== "undefined" && process.env?.NODE_ENV === "test") {
  const now = Date.now();
  const iso = (h) => new Date(now + h * 3600000).toISOString().slice(0, 16).replace("T", " ") + ":00";
  const fc = { daily: {}, hourly: {} };
  const soon = iso(48); // 48h out → hourly
  const far = iso(24 * 6); // 6 days → daily
  fc.hourly[`${soon.slice(0, 10)}T${soon.slice(11, 13)}:00`] = { code: 0, temp: 70, precip: 5 };
  fc.daily[far.slice(0, 10)] = { code: 3, hi: 80, lo: 60, precip: 20 };
  console.assert(pickForecast(fc, soon)?.kind === "hour", "within 72h → hourly");
  console.assert(pickForecast(fc, far)?.kind === "day", "3–10 days → daily range");
  console.assert(pickForecast(fc, iso(24 * 20)) === null, "past 10 days → nothing");
  console.assert(pickForecast(fc, iso(-10)) === null, "past session → nothing");
  console.assert(heatAdvisory({ kind: "hour", feels: 106 })?.tone === "black", "106F → black");
  console.assert(heatAdvisory({ kind: "day", feelsHi: 96, feelsLo: 60 })?.tone === "red", "96F day → red");
  console.assert(heatAdvisory({ kind: "hour", feels: 70 }) === null, "70F → none");
  console.assert(heatAdvisory({ kind: "hour", feels: 5 })?.tone === "blue", "5F → cold");
}
